/**
 * BalanceSummaryCard Component
 *
 * Balance summary card for Recursos Dashboard displaying financial metrics.
 * Shows label, formatted currency value with money bag icon, subtitle, and percentage change badge.
 *
 * Design specs from Figma:
 * - Background: #15203C (dark blue)
 * - Border radius: 20px
 * - Padding: 30px
 * - Height: 158px
 * - Money bag icon (30x30) on right side of value
 * - Displays: Label + Value + Icon + Subtitle + Percentage Badge
 */

import { cn } from "@/lib/class-control";
import { Coins } from "lucide-react";

interface BalanceSummaryCardProps {
  /** Card title/label */
  label: string;

  /** Numeric value to display (in BRL) */
  value: number;

  /** Subtitle text below value */
  subtitle: string;

  /** Percentage change value */
  percentage: number;

  /** Trend direction (up = positive/green, down = negative/red) */
  trend: 'up' | 'down';
}

/**
 * Formats number as Brazilian Real currency
 * Example: 589854.98 → "R$ 589.854,98"
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function BalanceSummaryCard({
  label,
  value,
  subtitle,
  percentage,
  trend,
}: BalanceSummaryCardProps) {
  const isPositive = trend === 'up';

  return (
    <div className="bg-[#15203C] rounded-lg p-4 flex flex-col justify-center gap-2">
      {/* Label */}
      <p className="text-[10px] text-white font-medium">
        {label}
      </p>

      {/* Value + Icon */}
      <div className="flex items-center justify-between">
        <p className="text-lg font-bold text-white leading-none">
          {formatCurrency(value)}
        </p>
        {/* Money Bag Icon */}
        <Coins size={20} className="text-[#13D480]" strokeWidth={1.5} />
      </div>

      {/* Subtitle + Percentage Badge */}
      <div className="flex items-center justify-between gap-2">
        {/* Subtitle */}
        <p className="text-[10px] text-[#888888]">
          {subtitle}
        </p>

        {/* Percentage Badge */}
        {percentage !== 0 && (
          <div className="bg-[#0C1427] rounded-full px-2 py-0.5">
            <p
              className={cn(
                "text-[10px] font-medium",
                isPositive ? "text-[#13D480]" : "text-[#DC4426]"
              )}
            >
              {isPositive ? '+' : ''}{percentage}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
